import { useEffect, useRef, useState } from "react";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";

import {
  canSendSiteMessage,
  getJobStepStates,
  getRemainingForNextMessage,
  isJobActive,
} from "./provider-site";
import { SiteHeader } from "./site-header";
import { SiteLayout, SiteLoadingCard } from "./site-layout";
import { useMyProviderSite, useSendSiteMessage } from "./site.hooks";
import { notifyError } from "./site.notify";
import { SITE_ROUTE } from "./site.routes";
import { openUrl } from "./site.web";
import { useJobPolling } from "./use-job-polling";
import { useSiteTranslations } from "./use-site-translations";

function SiteChat({ profileUuid }: { profileUuid: string }) {
  const { t } = useSiteTranslations();
  const { data, loading, startPolling, stopPolling } = useMyProviderSite(profileUuid);
  const [sendMessage, { loading: isSending }] = useSendSiteMessage();
  const [draft, setDraft] = useState("");
  const endRef = useRef<HTMLDivElement>(null);
  const site = data?.myProviderSite ?? null;
  const job = site?.activeJob ?? null;
  const isWorking = isJobActive(job);
  const limitReached = getRemainingForNextMessage(site) === 0 && !isWorking;
  const canSend = !isSending && canSendSiteMessage(site, draft);
  const messages = site?.messages ?? [];
  const lastAgentMessageUuid = [...messages]
    .reverse()
    .find((message) => message.role === "AGENT")?.uuid;

  useJobPolling(isWorking, startPolling, stopPolling);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  }, [messages.length, isWorking]);

  async function onSend() {
    if (!canSend) return;
    try {
      await sendMessage({ variables: { profileUuid, text: draft.trim() } });
      setDraft("");
    } catch {
      notifyError(t.errors.sendFailedTitle, t.errors.tryAgain);
    }
  }

  if (loading && !data) {
    return <SiteLoadingCard />;
  }

  return (
    <SoftBox display="flex" flexDirection="column" gap={3}>
      <SiteHeader title={t.chat.title} description={t.chat.subtitle} backTo={SITE_ROUTE} />
      <Card data-testid="provider-site-chat-screen">
        <SoftBox
          p={3}
          display="flex"
          flexDirection="column"
          gap={1.5}
          sx={{ minHeight: "20rem", maxHeight: "60vh", overflowY: "auto" }}
        >
          {messages.length === 0 ? (
            <SoftTypography variant="button" color="text" fontWeight="regular" textAlign="center">
              {t.chat.intro}
            </SoftTypography>
          ) : (
            messages.map((message) => {
              const isOwn = message.role === "PROVIDER";
              return (
                <SoftBox
                  key={message.uuid}
                  display="flex"
                  justifyContent={isOwn ? "flex-end" : "flex-start"}
                >
                  <SoftBox
                    maxWidth="84%"
                    px={2}
                    py={1.5}
                    borderRadius="xl"
                    variant={isOwn ? "gradient" : undefined}
                    bgColor={isOwn ? "info" : "grey-100"}
                    display="flex"
                    flexDirection="column"
                    gap={1.5}
                  >
                    <SoftTypography
                      variant="button"
                      fontWeight="regular"
                      color={isOwn ? "white" : "dark"}
                      sx={{ whiteSpace: "pre-wrap", userSelect: "text" }}
                    >
                      {message.text}
                    </SoftTypography>
                    {!isOwn &&
                    message.uuid === lastAgentMessageUuid &&
                    site?.hasUnpublishedChanges &&
                    site.draftPreviewUrl ? (
                      <SoftButton
                        variant="outlined"
                        color="info"
                        size="small"
                        onClick={() => openUrl(site.draftPreviewUrl as string)}
                        data-testid="provider-site-chat-preview"
                      >
                        {t.chat.previewDraft}
                      </SoftButton>
                    ) : null}
                  </SoftBox>
                </SoftBox>
              );
            })
          )}
          {isWorking ? (
            <SoftBox data-testid="provider-site-chat-working" display="flex">
              <SoftBox
                maxWidth="84%"
                px={2}
                py={1.5}
                borderRadius="xl"
                bgColor="grey-100"
                display="flex"
                flexDirection="column"
                gap={1}
              >
                <SoftTypography variant="button" fontWeight="bold">
                  {t.chat.working}
                </SoftTypography>
                {getJobStepStates(job?.step).map(({ step, state }) => (
                  <SoftBox key={step} display="flex" alignItems="center" gap={1}>
                    {state === "current" ? (
                      <CircularProgress size={12} color="info" />
                    ) : (
                      <SoftBox
                        width="0.5rem"
                        height="0.5rem"
                        mx="2px"
                        borderRadius="50%"
                        bgColor={state === "done" ? "info" : "grey-400"}
                      />
                    )}
                    <SoftTypography
                      variant="button"
                      fontWeight="regular"
                      color={state === "next" ? "text" : "dark"}
                    >
                      {t.chat.steps[step]}
                    </SoftTypography>
                  </SoftBox>
                ))}
                <SoftTypography variant="caption" color="text">
                  {t.chat.closeHint}
                </SoftTypography>
              </SoftBox>
            </SoftBox>
          ) : null}
          <div ref={endRef} />
        </SoftBox>
        <Divider sx={{ my: 0 }} />
        <SoftBox
          component="form"
          p={3}
          display="flex"
          flexDirection="column"
          gap={1}
          onSubmit={(event: { preventDefault: () => void }) => {
            event.preventDefault();
            void onSend();
          }}
        >
          {limitReached ? (
            <SoftTypography variant="caption" color="text" data-testid="provider-site-chat-limit">
              {t.chat.limitReached}
            </SoftTypography>
          ) : null}
          <SoftBox display="flex" gap={1.5} alignItems="flex-end">
            <SoftBox flexGrow={1}>
              <SoftInput
                multiline
                maxRows={5}
                value={draft}
                disabled={isWorking || limitReached}
                placeholder={isWorking ? t.chat.waitingPlaceholder : t.chat.placeholder}
                onChange={(event: { target: { value: string } }) => setDraft(event.target.value)}
                onKeyDown={(event: {
                  key: string;
                  shiftKey: boolean;
                  preventDefault: () => void;
                }) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    void onSend();
                  }
                }}
                inputProps={{
                  "aria-label": t.chat.inputLabel,
                  "data-testid": "provider-site-chat-input",
                }}
              />
            </SoftBox>
            <SoftButton
              type="submit"
              variant="gradient"
              color="info"
              iconOnly
              circular
              disabled={!canSend}
              aria-label={t.chat.send}
              data-testid="provider-site-chat-send"
            >
              {isSending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Icon>arrow_upward</Icon>
              )}
            </SoftButton>
          </SoftBox>
        </SoftBox>
      </Card>
    </SoftBox>
  );
}

// "Дизайнер сайту": the chat with Matcha's AI designer, which changes only the design.
export function SiteChatPage() {
  return <SiteLayout>{({ profileUuid }) => <SiteChat profileUuid={profileUuid} />}</SiteLayout>;
}
